/* New subs — compact layout. The old Triggers rail is gone (it only ever held
   New Subscription). A "Send to new subscribers" row, a toolbar (summary, Copy
   from account, Add message), then one card with a row per message: wait
   stepper + unit, the message, attached content (opens the shared media
   picker) with its price, and reorder/delete. Every change saves on its own:
   only the messages that changed are written. */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import type { AccountAutomatedAssignment } from '../../../api/client';
import { useAutosave } from '../../../hooks/useAutosave';
import { Btn, Card, ErrorLine, Icon, Loading, NumInput, PageFrame, Row, SaveStatus, Seg, Stack, Toggle, Warn } from '../../ChatAssistant/compact/kit';
import { MediaPicker } from '../../ChatAssistant/compact/MediaPicker';
import { ThumbStrip } from '../../ChatAssistant/compact/ThumbStrip';
import { useResolveIds } from '../../ChatAssistant/compact/resolveIds';
import { useSalesCatalogue } from '../../ChatAssistant/SalesLadder/useSalesCatalogue';
import { isVideoItem } from '../../ChatAssistant/SalesLadder/MediaBadge';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { MIN_PAID_PRICE, UNITS, blankMsg, durParts, msgOf, planSave, priceInvalid, secsOf, storedOf, type Msg, type Stored } from './subsModel';

interface Draft { enabled: boolean; msgs: Msg[] }

const hasBody = (m: Msg) => !!m.text.trim() || m.contentIds.length > 0;

export function NewSubsPage({ accountId, onCopy }: { accountId: string; onCopy: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [draft, setDraft] = useState<Draft>({ enabled: false, msgs: [] });
  const [picking, setPicking] = useState<string | null>(null);
  const assignment = useRef<AccountAutomatedAssignment | null>(null);
  const saved = useRef<Stored[]>([]);

  useEffect(() => {
    let off = false;
    (async () => {
      const a = await api.getAccountAutomatedAssignment(accountId).catch(() => null);
      const msgs = a?.list_id != null ? await api.getAutomatedMessages(accountId, a.list_id) : [];
      if (off) return;
      assignment.current = a;
      saved.current = msgs.map(storedOf);
      setDraft({ enabled: a?.is_enabled ?? false, msgs: msgs.map(msgOf) });
      setState('ready');
    })().catch(() => { if (!off) setState('failed'); });
    return () => { off = true; };
  }, [accountId]);

  const auto = useAutosave<Draft>({
    value: draft,
    enabled: state === 'ready',
    delay: 700,
    validate: (d) => (d.msgs.some((m) => priceInvalid(m.price)) ? t('am.priceMin', { min: MIN_PAID_PRICE }) : null),
    save: async (d) => {
      // Messages with nothing in them aren't sent, so they aren't stored either.
      const live = d.msgs.filter(hasBody);
      let a = assignment.current;
      if (a?.list_id == null) {
        if (!live.length && !d.enabled && !a) return;
        const list = await api.createAutomatedMessagesList(accountId, { list_name: `auto_new_subscription_${Date.now()}` });
        a = a
          ? await api.updateAccountAutomatedAssignment(accountId, { list_id: list.list_id, is_enabled: d.enabled })
          : await api.assignAutomatedMessagesToAccount(accountId, { list_id: list.list_id, is_enabled: d.enabled });
        assignment.current = a;
      } else if (a.is_enabled !== d.enabled) {
        a = await api.updateAccountAutomatedAssignment(accountId, { is_enabled: d.enabled });
        assignment.current = a;
      }
      const listId = a.list_id!;

      const plan = planSave(saved.current, live);
      const byId = new Map(saved.current.map((s) => [s.id, s]));
      for (const id of plan.del) { await api.deleteAutomatedMessage(accountId, id); byId.delete(id); }
      if (plan.tempOrders.length) {
        await api.reorderAutomatedMessages(accountId, listId, plan.tempOrders);
        await api.reorderAutomatedMessages(accountId, listId, plan.finalOrders);
        plan.finalOrders.forEach((o) => { byId.set(o.message_id, { ...byId.get(o.message_id)!, order: o.message_order }); });
      }
      for (const p of plan.patch) byId.set(p.id, storedOf(await api.updateAutomatedMessage(accountId, p.id, p.body)));
      const created = new Map<string, number>();
      for (const c of plan.create) {
        const m = await api.createAutomatedMessage(accountId, listId, c.body);
        byId.set(m.message_id, storedOf(m));
        created.set(c.key, m.message_id);
      }
      saved.current = [...byId.values()];

      // New messages get their server id; emptied ones were deleted and lose theirs.
      const dropped = new Set(plan.del);
      const withIds = (m: Msg): Msg => (created.has(m.key) ? { ...m, id: created.get(m.key)! } : m.id !== null && dropped.has(m.id) ? { ...m, id: null } : m);
      setDraft((cur) => ({ ...cur, msgs: cur.msgs.map(withIds) }));
      return { ...d, msgs: d.msgs.map(withIds) };
    },
  });

  const cat = useSalesCatalogue(accountId);
  useResolveIds(accountId, cat, draft.msgs.flatMap((m) => m.contentIds));

  const msgs = draft.msgs;
  const n = msgs.length;
  const setMsgs = (fn: (m: Msg[]) => Msg[]) => setDraft((d) => ({ ...d, msgs: fn(d.msgs) }));
  const edit = (k: number, p: Partial<Msg>) => setMsgs((a) => a.map((m, j) => (j === k ? { ...m, ...p } : m)));
  const move = (k: number, dir: -1 | 1) => setMsgs((a) => {
    const j = k + dir;
    if (j < 0 || j >= a.length) return a;
    const next = a.slice();
    const [x] = next.splice(k, 1);
    next.splice(j, 0, x!);
    return next;
  });
  const dur = (s: number) => (s ? durParts(s).map((p) => t(`am.u.${p.unit}`, { count: p.n })).join(' ') : t('am.u.sec', { count: 0 }));
  const total = msgs.reduce((a, m) => a + secsOf(m), 0);
  const mix = (ids: string[]) => {
    const items = ids.map((id) => cat.byId.get(id)).filter((x) => !!x);
    if (items.length < ids.length) return t('am.items', { count: ids.length });
    const v = items.filter((i) => isVideoItem(i!.type)).length;
    return [ids.length - v ? t('picker.photos', { count: ids.length - v }) : '', v ? t('picker.videos', { count: v }) : ''].filter(Boolean).join(' · ');
  };
  const pickingMsg = msgs.find((m) => m.key === picking);

  if (state === 'loading') return <PageFrame headline={t('am.subs.title')} desc={t('am.subs.desc')}><Loading /></PageFrame>;
  if (state === 'failed') return <PageFrame headline={t('am.subs.title')} desc={t('am.subs.desc')}><ErrorLine>{t('loadFailed')}</ErrorLine></PageFrame>;

  return (
    <PageFrame headline={t('am.subs.title')} desc={t('am.subs.desc')} right={<SaveStatus status={auto.status} error={auto.error} onRetry={auto.retry} idleLabel />}>
      <Stack gap={12}>
        <Card>
          <Row label={t('am.subs.send')} help={draft.enabled ? t('am.subs.on', { count: n }) : t('am.subs.off')}>
            <Toggle on={draft.enabled} onChange={(v) => { setDraft((d) => ({ ...d, enabled: v })); window.setTimeout(auto.flush, 0); }} />
          </Row>
        </Card>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ font: f(500, 12), color: C.sub }}>{n ? t('am.subs.summary', { count: n, last: dur(total) }) : t('am.subs.noMessages')}</span>
          <span style={{ flex: 1 }} />
          <Btn icon="copy" title={t('am.subs.copyTip')} onClick={onCopy}>{t('am.subs.copy')}</Btn>
          <Btn kind="primary" icon="plus" onClick={() => setMsgs((a) => [...a, blankMsg()])}>{t('am.subs.add')}</Btn>
        </div>

        <div style={{ background: C.divider, border: `1px solid ${C.border}`, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', opacity: draft.enabled ? 1 : 0.45, transition: 'opacity .15s' }}>
          {msgs.map((m, k) => {
            const L = m.contentIds.length;
            return (
              <div key={m.key} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) auto', gap: 12, padding: '12px 10px 12px 14px', background: C.card }}>
                <span style={{ width: 22, height: 22, boxSizing: 'border-box', borderRadius: 99, border: `1.5px solid ${C.purple}`, display: 'flex', alignItems: 'center', justifyContent: 'center', font: f(700, 11), color: C.purplePale, marginTop: 3 }}>{k + 1}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Icon name="clock" size={13} color={C.muted} />
                    <span style={{ font: f(500, 12), color: C.sub }}>{t('am.subs.wait')}</span>
                    <WaitStepper value={m.value} onChange={(v) => edit(k, { value: v })} />
                    <Seg value={m.unit} onChange={(u) => edit(k, { unit: u })} options={UNITS.map((u) => ({ id: u.id, label: t(`am.unit.${u.id}`) }))} />
                    <span style={{ font: f(400, 12), color: C.sub, whiteSpace: 'nowrap' }}>{k ? t('am.subs.afterMsg', { n: k }) : t('am.subs.afterSub')}</span>
                  </div>
                  <textarea value={m.text} onChange={(e) => edit(k, { text: e.target.value })} placeholder={t('am.subs.textPh')} style={{ minHeight: 52, resize: 'vertical', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '8px 10px', color: C.text, font: f(400, 12.5, 1.5), outline: 'none', width: '100%' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Icon name="paperclip" size={13} color={C.muted} />
                    <ThumbStrip accountId={accountId} ids={m.contentIds} byId={cat.byId} max={4} onClick={() => setPicking(m.key)} />
                    <button type="button" onClick={() => setPicking(m.key)} className="cpt-hover" style={{ height: 26, padding: '0 10px', background: 'transparent', border: `1px dashed ${C.borderHi}`, borderRadius: 6, color: C.purplePale, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>{L ? t('am.subs.edit') : t('am.subs.attach')}</button>
                    <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{L ? mix(m.contentIds) : t('optional')}</span>
                    {L > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                        <span style={{ font: f(500, 12), color: C.sub }}>{t('am.subs.price')}</span>
                        <span style={{ font: f(500, 12), color: C.muted }}>$</span>
                        <NumInput value={m.price} allowEmpty min={0} width={64} placeholder={t('am.subs.free')} title={t('am.subs.priceTip', { min: MIN_PAID_PRICE })} onChange={(v) => edit(k, { price: v })} />
                      </span>
                    )}
                  </div>
                  {priceInvalid(m.price) && <Warn>{t('am.priceMin', { min: MIN_PAID_PRICE })}</Warn>}
                  {!hasBody(m) && <Warn>{t('am.subs.empty')}</Warn>}
                </div>
                <div style={{ display: 'flex', gap: 2, alignSelf: 'flex-start' }}>
                  <IconBtn icon="chevron-up" title={t('nf.moveUp')} disabled={k === 0} onClick={() => move(k, -1)} />
                  <IconBtn icon="chevron-down" title={t('nf.moveDown')} disabled={k === n - 1} onClick={() => move(k, 1)} />
                  <IconBtn icon="trash-2" title={t('am.subs.delete')} onClick={() => setMsgs((a) => a.filter((_, j) => j !== k))} />
                </div>
              </div>
            );
          })}
          {!n && <div style={{ padding: 24, textAlign: 'center', background: C.card, font: f(400, 12), color: C.muted }}>{t('am.subs.none')}</div>}
        </div>
      </Stack>

      {pickingMsg && (
        <MediaPicker
          accountId={accountId}
          title={t('am.subs.pickTitle', { n: msgs.indexOf(pickingMsg) + 1 })}
          initialSelected={pickingMsg.contentIds}
          allowEmpty
          onClose={() => setPicking(null)}
          onConfirm={(ids) => {
            setMsgs((a) => a.map((m) => (m.key === pickingMsg.key ? { ...m, contentIds: ids, price: ids.length ? m.price : null } : m)));
            setPicking(null);
          }}
        />
      )}
    </PageFrame>
  );
}

/** − value + pill with a typeable value. */
function WaitStepper({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const b = { width: 26, height: 26, border: 0, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: 28, flex: 'none', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6 }}>
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} className="cpt-hover-text" style={b}><Icon name="minus" size={12} /></button>
      <input
        value={value}
        inputMode="numeric"
        onChange={(e) => { const v = parseInt(e.target.value.replace(/\D/g, ''), 10); onChange(Number.isNaN(v) ? 0 : Math.min(9999, v)); }}
        style={{ width: 48, height: 24, boxSizing: 'border-box', textAlign: 'center', background: 'transparent', border: 0, padding: 0, color: C.text, font: f(600, 12.5), fontVariantNumeric: 'tabular-nums', outline: 'none' }}
      />
      <button type="button" onClick={() => onChange(Math.min(9999, value + 1))} className="cpt-hover-text" style={b}><Icon name="plus" size={12} /></button>
    </div>
  );
}

export function IconBtn({ icon, title, disabled, onClick }: { icon: string; title: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick} className={disabled ? undefined : 'cpt-hover-row cpt-hover-text'} style={{ width: 26, height: 26, flex: 'none', borderRadius: 6, border: 0, background: 'transparent', color: C.muted, cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: disabled ? 0.3 : 1 }}>
      <Icon name={icon} size={13} />
    </button>
  );
}
